import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import {
  MapPin,
  Phone,
  Award,
  Star,
  Clock,
  CalendarDays,
  CheckCircle,
  Loader2,
  Send,
  ArrowLeft,
  Instagram,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const placeholderPhoto = "https://images.pexels.com/photos/3993449/pexels-photo-3993449.jpeg?auto=compress&cs=tinysrgb&w=800";

const timeSlots = [
  "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM",
  "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM",
  "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM",
  "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM",
  "5:00 PM", "5:30 PM",
];

export const AlumniProfilePage = () => {
  const { id } = useParams();
  const [alumni, setAlumni] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBooking, setShowBooking] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [selectedTime, setSelectedTime] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBooked, setIsBooked] = useState(false);
  const [form, setForm] = useState({ client_name: "", client_phone: "", client_email: "", notes: "" });

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchAlumni();
  }, [id]);

  const fetchAlumni = async () => {
    try {
      const res = await axios.get(`${API}/alumni/${id}`);
      setAlumni(res.data);
    } catch {
      setAlumni(null);
    } finally {
      setLoading(false);
    }
  };

  const toggleService = (s) => {
    setSelectedServices((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  const handleFormChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        alumni_id: alumni.id,
        alumni_name: alumni.name,
        services: selectedServices,
        date: selectedDate ? selectedDate.toISOString().split("T")[0] : "",
        time: selectedTime,
        ...form,
      };
      const res = await axios.post(`${API}/alumni/book`, payload);
      if (res.data.success) {
        setIsBooked(true);
        toast.success("Booking submitted!", { description: `${alumni.name} will be in touch.` });
      }
    } catch {
      toast.error("Failed to submit booking");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin" />
      </div>
    );
  }

  if (!alumni) {
    return (
      <div className="pt-20">
        <section className="py-24 bg-[#050505] text-center">
          <h2 className="font-heading text-2xl text-white mb-4">Profile Not Found</h2>
          <Link to="/alumni" className="text-[#C5A059] hover:underline">Back to Directory</Link>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="alumni-profile-page" className="pt-20">
      {/* Header */}
      <section className="py-12 md:py-16 bg-[#050505]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Link to="/alumni" className="inline-flex items-center gap-2 text-[#C5A059] text-sm hover:underline mb-8">
            <ArrowLeft size={14} /> Back to Directory
          </Link>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Photo */}
            <div className="md:col-span-1">
              <div className="aspect-square overflow-hidden border border-[#262626]">
                <img
                  src={alumni.photo_url || placeholderPhoto}
                  alt={alumni.name}
                  className="w-full h-full object-cover"
                  data-testid="alumni-profile-photo"
                />
              </div>
            </div>

            {/* Info */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-2">
                {alumni.featured && (
                  <span className="bg-[#C5A059] text-[#050505] text-xs font-semibold px-3 py-1 flex items-center gap-1">
                    <Star size={10} fill="currentColor" /> Featured
                  </span>
                )}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl text-white mb-1" data-testid="alumni-profile-name">
                {alumni.name}
              </h1>
              {alumni.business_name && (
                <p className="font-body text-lg text-[#C5A059] mb-4">{alumni.business_name}</p>
              )}

              <div className="flex items-center gap-2 text-[#A3A3A3] text-sm mb-4">
                <MapPin size={14} className="text-[#C5A059]" />
                <span>{alumni.location}, {alumni.region}</span>
              </div>

              {alumni.bio && (
                <p className="font-body text-[#A3A3A3] leading-relaxed mb-6">{alumni.bio}</p>
              )}

              {/* Contact links */}
              <div className="flex flex-wrap gap-3 mb-6">
                {alumni.phone && (
                  <a href={`tel:${alumni.phone}`} className="flex items-center gap-2 border border-[#262626] px-4 py-2 text-sm text-[#A3A3A3] hover:border-[#C5A059] hover:text-white transition-all">
                    <Phone size={14} className="text-[#C5A059]" /> {alumni.phone}
                  </a>
                )}
                {alumni.instagram && (
                  <a href={`https://instagram.com/${alumni.instagram.replace('@', '')}`} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 border border-[#262626] px-4 py-2 text-sm text-[#A3A3A3] hover:border-[#C5A059] hover:text-white transition-all">
                    <Instagram size={14} className="text-[#C5A059]" /> {alumni.instagram}
                  </a>
                )}
              </div>

              {/* Services */}
              <div className="mb-6">
                <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-3">
                  Services Offered
                </h3>
                <div className="flex flex-wrap gap-2">
                  {alumni.services_offered.map((s) => (
                    <span key={s} className="bg-[#C5A059]/10 text-[#C5A059] text-xs font-semibold uppercase tracking-wider px-3 py-1">
                      {s}
                    </span>
                  ))}
                </div>
              </div>

              {/* Courses */}
              <div className="mb-6">
                <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-3">
                  Luxury Beauty Certified
                </h3>
                <div className="flex flex-wrap gap-2">
                  {alumni.courses_completed.map((c) => (
                    <span key={c} className="flex items-center gap-1 text-xs text-[#A3A3A3]">
                      <Award size={10} className="text-[#C5A059]" /> {c}
                    </span>
                  ))}
                  {alumni.year_completed && (
                    <span className="text-xs text-[#A3A3A3]">({alumni.year_completed})</span>
                  )}
                </div>
              </div>

              <button
                onClick={() => setShowBooking(true)}
                data-testid="alumni-book-btn"
                className="bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase px-8 py-4 hover:brightness-110 transition-all"
              >
                Book with {alumni.name.split(" ")[0]}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Section */}
      {showBooking && !isBooked && (
        <section className="py-12 md:py-16 bg-[#0A0A0A]" data-testid="alumni-booking-section">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <h2 className="font-heading text-2xl text-white mb-8">
              Book with <span className="text-[#C5A059]">{alumni.name}</span>
            </h2>

            <form onSubmit={handleBooking} className="space-y-8">
              {/* Select Services */}
              <div className="bg-[#121212] border border-[#262626] p-6">
                <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-4">
                  Select Services *
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {alumni.services_offered.map((service) => {
                    const sel = selectedServices.includes(service);
                    return (
                      <button
                        key={service}
                        type="button"
                        onClick={() => toggleService(service)}
                        data-testid="alumni-booking-service"
                        className={`p-3 border text-left text-sm font-body transition-all flex items-center gap-2 ${
                          sel
                            ? "border-[#C5A059] bg-[#C5A059]/10 text-white"
                            : "border-[#262626] text-[#A3A3A3] hover:border-[#C5A059]/50"
                        }`}
                      >
                        <div className={`w-4 h-4 flex-shrink-0 border flex items-center justify-center ${
                          sel ? "border-[#C5A059] bg-[#C5A059]" : "border-[#A3A3A3]"
                        }`}>
                          {sel && <CheckCircle size={10} className="text-[#050505]" />}
                        </div>
                        {service}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Date & Time */}
              <div className="bg-[#121212] border border-[#262626] p-6">
                <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-4">
                  Preferred Date & Time *
                </h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center gap-2 text-sm text-[#A3A3A3] mb-3">
                      <CalendarDays size={14} className="text-[#C5A059]" /> Select a Date
                    </div>
                    <div className="bg-[#0A0A0A] border border-[#262626] p-3 inline-block">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={[{ before: new Date() }]}
                        className="text-white"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-sm text-[#A3A3A3] mb-3">
                      <Clock size={14} className="text-[#C5A059]" /> Select a Time
                    </div>
                    {selectedDate ? (
                      <div className="grid grid-cols-3 gap-2">
                        {timeSlots.map((time) => (
                          <button
                            key={time}
                            type="button"
                            onClick={() => setSelectedTime(time)}
                            className={`p-2.5 border text-center text-xs font-body transition-all ${
                              selectedTime === time
                                ? "border-[#C5A059] bg-[#C5A059]/10 text-white"
                                : "border-[#262626] text-[#A3A3A3] hover:border-[#C5A059]/50"
                            }`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-[#A3A3A3]">Select a date first</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="bg-[#121212] border border-[#262626] p-6">
                <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-4">
                  Your Information
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Name *</label>
                    <input type="text" name="client_name" value={form.client_name} onChange={handleFormChange} required
                      data-testid="alumni-booking-name" placeholder="Your name" className="w-full luxury-input" />
                  </div>
                  <div>
                    <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Phone *</label>
                    <input type="tel" name="client_phone" value={form.client_phone} onChange={handleFormChange} required
                      data-testid="alumni-booking-phone" placeholder="Phone number" className="w-full luxury-input" />
                  </div>
                  <div>
                    <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Email</label>
                    <input type="email" name="client_email" value={form.client_email} onChange={handleFormChange}
                      data-testid="alumni-booking-email" placeholder="Email (optional)" className="w-full luxury-input" />
                  </div>
                </div>
                <div className="mt-6">
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Notes</label>
                  <textarea name="notes" value={form.notes} onChange={handleFormChange} rows={3}
                    data-testid="alumni-booking-notes" placeholder="Any special requests..."
                    className="w-full luxury-input resize-none" />
                </div>
              </div>

              <button
                type="submit"
                disabled={selectedServices.length === 0 || !selectedDate || !selectedTime || !form.client_name || !form.client_phone || isSubmitting}
                data-testid="alumni-booking-submit"
                className="w-full bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase py-4 hover:brightness-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {isSubmitting ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Booking...</>
                ) : (
                  <><Send className="w-5 h-5" /> Request Booking</>
                )}
              </button>
            </form>
          </div>
        </section>
      )}

      {/* Booked Confirmation */}
      {isBooked && (
        <section className="py-16 bg-[#0A0A0A]">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#121212] border border-[#262626] p-12 text-center"
              data-testid="alumni-booking-success"
            >
              <CheckCircle className="w-16 h-16 text-[#C5A059] mx-auto mb-6" />
              <h3 className="font-heading text-2xl text-white mb-4">Booking Requested</h3>
              <p className="font-body text-[#A3A3A3] mb-2">
                Your booking request with <strong className="text-white">{alumni.name}</strong> has been submitted.
              </p>
              <p className="font-body text-sm text-[#A3A3A3] mb-8">
                They will contact you at <strong className="text-white">{form.client_phone}</strong> to confirm.
              </p>
              <Link to="/alumni" className="text-[#C5A059] text-sm tracking-wider uppercase hover:underline">
                Back to Directory
              </Link>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  );
};

export default AlumniProfilePage;
